import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, FileText, ShieldCheck, ShieldAlert, ShieldQuestion, MailCheck, MailWarning } from "lucide-react";
import api, { API, formatApiError } from "../lib/api";

const FILTERS = [["pending", "In attesa"], ["rejected", "Rifiutati"]];

function ViesBadge({ vies }) {
  if (vies?.valid) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" /> Valida su VIES</span>;
  if (vies?.unreachable) return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><ShieldQuestion className="w-3.5 h-3.5" /> VIES non raggiungibile</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700"><ShieldAlert className="w-3.5 h-3.5" /> Non presente su VIES</span>;
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm text-slate-900">{children}</div>
    </div>
  );
}

export default function AdminVerificationQueue() {
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get(`/admin/verifications?status=${status}`).then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { setList(null); load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const review = async (u, decision) => {
    let body;
    if (decision === "reject") {
      const reason = window.prompt(`Motivo del rifiuto per ${u.business_name || u.email} (lo riceve via email):`, "");
      if (!reason || reason.trim().length < 3) return;
      body = { reason: reason.trim() };
    }
    setBusy(u.id);
    try {
      await api.post(`/admin/verifications/${u.id}/${decision}`, body);
      toast.success(decision === "approve" ? "Account approvato: l'utente riceve notifica ed email" : "Account rifiutato: l'utente riceve notifica ed email");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="ga-card p-5" data-testid="admin-verification-queue">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Aziende da verificare {list && `(${list.length})`}</div>
        <div className="flex gap-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${status === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:bg-slate-50"}`} data-testid={`verification-filter-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-600">I fornitori offrono solo dopo la tua approvazione. Gli acquirenti con P.IVA valida su VIES ordinano subito; qui arrivano quelli non presenti su VIES o registrati mentre VIES non rispondeva, con la visura camerale se l'hanno caricata.</p>
      {list === null && <div className="mt-3 text-sm text-slate-500">Caricamento...</div>}
      {list?.length === 0 && <div className="mt-3 text-sm text-slate-500" data-testid="verification-queue-empty">Nessuna azienda in questo stato.</div>}
      <div className="mt-3 space-y-3">
        {(list || []).map(u => (
          <div key={u.id} className="rounded-2xl border border-slate-200 p-4" data-testid={`verification-item-${u.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">{u.business_name || u.name} <span className="ml-1 text-[10px] rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5 align-middle">{u.role === "supplier" ? "Fornitore" : "Acquirente"}</span></div>
                <div className="text-xs text-slate-500">{u.email} · referente {u.name || "—"} · iscritto il {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => review(u, "approve")} disabled={busy === u.id} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid={`verification-approve-${u.id}`}><Check className="w-3.5 h-3.5" /> Approva</button>
                {status === "pending" && <button onClick={() => review(u, "reject")} disabled={busy === u.id} className="inline-flex items-center gap-1 rounded-full border border-red-300 text-red-700 px-3 py-1.5 text-xs font-semibold hover:bg-red-50 disabled:opacity-50" data-testid={`verification-reject-${u.id}`}><X className="w-3.5 h-3.5" /> Rifiuta</button>}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Field label="Nome dichiarato">{u.business_name || "—"}</Field>
              <Field label="Nome registrato su VIES">{u.vies?.name || "—"}</Field>
              <Field label="Indirizzo su VIES">{u.vies?.address || "—"}</Field>
              <Field label="Partita IVA"><span className="font-mono">{u.vat_number || "—"}</span><div><ViesBadge vies={u.vies} />{u.vies?.checked_at && <span className="ml-1 text-[11px] text-slate-500">· {new Date(u.vies.checked_at).toLocaleDateString("it-IT")}</span>}</div></Field>
              <Field label="Email">{u.email_verified
                ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><MailCheck className="w-3.5 h-3.5" /> Confermata</span>
                : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><MailWarning className="w-3.5 h-3.5" /> Da confermare</span>}</Field>
              <Field label="Visura camerale">{u.visura
                ? <a href={`${API}/files/${u.visura.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 underline" data-testid={`verification-visura-${u.id}`}><FileText className="w-3.5 h-3.5" /> Apri visura · {new Date(u.visura.uploaded_at).toLocaleDateString("it-IT")}</a>
                : <span className="text-xs text-slate-500">Non caricata</span>}</Field>
              {u.verification_reason && <div className="sm:col-span-2"><Field label="Motivo del rifiuto">{u.verification_reason}</Field></div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
